import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { RefreshCw, Check, PenTool } from 'lucide-react';

export default function SignaturePad({ onSignatureChange, label = "Applicant Signature" }) {
  const sigCanvas = useRef(null);
  const containerRef = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(400);

  // Resize canvas to fit container
  useEffect(() => {
    const updateCanvasWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setCanvasWidth(Math.min(containerWidth - 4, 800)); // -4 for border, max 800
      }
    };

    updateCanvasWidth();
    window.addEventListener('resize', updateCanvasWidth);
    return () => window.removeEventListener('resize', updateCanvasWidth);
  }, []);

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setIsSigned(false);
    onSignatureChange?.(null);
  };

  const handleEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      // Get signature as data URL (PNG)
      const dataUrl = sigCanvas.current.toDataURL('image/png');
      setIsSigned(true);
      onSignatureChange?.(dataUrl);
    }
  };

  return (
    <Card className="p-6 border-purple-100">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-purple-100 rounded-lg">
          <PenTool className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="font-semibold text-purple-900">{label}</h3>
          <p className="text-sm text-gray-500">Sign using your mouse or finger</p>
        </div>
      </div>

      <div className="relative" ref={containerRef}>
        {/* Signature Canvas */}
        <div 
          className="border-2 border-dashed border-purple-200 rounded-lg bg-white overflow-hidden"
          style={{ touchAction: 'none' }}
        >
          <SignatureCanvas
            ref={sigCanvas}
            penColor="#1f2937"
            canvasProps={{
              width: canvasWidth,
              height: 150,
              className: 'signature-canvas',
              style: { 
                width: '100%', 
                height: '150px',
                maxWidth: '100%',
                display: 'block'
              }
            }}
            onEnd={handleEnd}
          />
          
          {/* Signature line */}
          <div className="absolute bottom-8 left-6 right-6 border-b border-gray-300"></div>
          <div className="absolute bottom-2 left-6 text-xs text-gray-400">Sign above the line</div>
        </div>

        {/* Status indicator */}
        {isSigned && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
            <Check className="w-3 h-3" />
            Signed
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center mt-4">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearSignature}
          className="text-gray-600 border-gray-200"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Clear Signature
        </Button>
        
        {!isSigned && (
          <p className="text-sm text-amber-600 flex items-center gap-1">
            <span className="inline-block w-2 h-2 bg-amber-500 rounded-full"></span>
            Signature required
          </p>
        )}
      </div>

      {/* Legal text */}
      <p className="text-xs text-gray-500 mt-4 leading-relaxed">
        By signing above, I certify that all information provided in this application is true and 
        complete to the best of my knowledge. I understand that any false statements or omissions 
        may disqualify me from employment or be cause for dismissal.
      </p>
    </Card>
  );
}
